// src/components/Feedback.tsx

import React, { useState } from 'react';
import {
    TextField,
    Button,
    Container,
    Typography,
    Box,
    Paper,
    Alert,
    CircularProgress,
    Link,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Email } from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import api from '../services/api';

const Feedback: React.FC = () => {
    const [type, setType] = useState<string>('');
    const [content, setContent] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');
        try {
            const payload = {
                type,
                content,
            };
            await api.post('/user/contact', payload);
            setSuccessMessage('Your message has been submitted successfully.');
            setType('');
            setContent('');
        } catch (error) {
            setErrorMessage('Failed to submit your message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box mt={8} component={Paper} elevation={3} p={4} borderRadius="12px">
                <Typography
                    variant="h4"
                    gutterBottom
                    align="center"
                    sx={{
                        fontWeight: 600,
                        color: '#0D47A1',
                    }}
                >
                    Contact / Feedback / Bug
                </Typography>
                <Box
                    mt={2}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    flexDirection="column"
                >
                    <Box display="flex" alignItems="center" mb={1}>
                        <Email sx={{ mr: 1, color: '#0D47A1' }} />
                        <Link href="mailto:ignas@shmoney.ai" underline="hover" color="inherit">
                            ignas@shmoney.ai
                        </Link>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <XIcon sx={{ mr: 1, color: '#0D47A1' }} />
                        <Link
                            href="https://x.com/zer0tokens"
                            underline="hover"
                            color="inherit"
                            target="_blank"
                            rel="noopener"
                        >
                            @zer0tokens
                        </Link>
                    </Box>
                </Box>
                {errorMessage && (
                    <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
                {successMessage && (
                    <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
                        {successMessage}
                    </Alert>
                )}
                <form onSubmit={handleSubmit}>
                    {/* Type */}
                    <Box mt={2}>
                        <FormControl
                            fullWidth
                            variant="outlined"
                            required
                            disabled={isSubmitting}
                        >
                            <InputLabel id="type-label">Type</InputLabel>
                            <Select
                                labelId="type-label"
                                id="type"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                label="Type"
                                sx={{
                                    borderRadius: '8px',
                                }}
                            >
                                <MenuItem value="contact">Contact</MenuItem>
                                <MenuItem value="feedback">Feedback</MenuItem>
                                <MenuItem value="bug_report">Bug Report</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    {/* Content */}
                    <Box mt={2}>
                        <TextField
                            label="Content"
                            required
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={4}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            disabled={isSubmitting}
                            InputProps={{
                                style: {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Box>
                    <Box mt={3}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={isSubmitting}
                            sx={{
                                fontWeight: 600,
                                padding: '0.75rem',
                                fontSize: '1rem',
                                borderRadius: '20px',
                                textTransform: 'none',
                                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Submit'
                            )}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Container>
    );
};

export default Feedback;
